// src/pages/Dashboard.js
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Box,
  Typography,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Grid,
  alpha,
  Autocomplete,
  TextField,
  Skeleton,
} from '@mui/material';
import {
  NotificationsOff as FollowupMissedIcon,
  InfoOutlined as DetailsNotSharedIcon,
  PersonOff as NotInterestedIcon,
  EventBusy as SiteVisitNoShowIcon,
  FilterList as FilterIcon,
  TrendingUp as RecoverableNILeadsIcon,
  Speed as AvgRecoveryScoreIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import isoWeek from 'dayjs/plugin/isoWeek';

import { DashboardProvider, useDashboard } from '../../contexts/DashboardContext';
import { useAuth } from '../../contexts/AuthContext';
import StatsGrid from './StatsGrid';
import DateRangeFilter from '../../components/common/DateRangeFilter';
import AgentPerformanceStats from './AgentPerformanceStats';
import NotInterestedReasonsChart from './NotInterestedReasonsChart';
import PendingActionStats from './PendingActionStats';
import ActiveLeadFunnel from './ActiveLeadFunnel';
import CallPerformanceCards from './CallPerformanceCards';
import ActivityTicker from '../../components/common/ActivityTicker';
import LiveAgentLocation from './LiveAgentLocation';
import dayjs from 'dayjs';

dayjs.extend(isoWeek);

const defaultDateRange = {
  filter: { id: 'Today', label: 'Today' },
  currentStart: dayjs().startOf('day'),
  currentEnd: dayjs().endOf('day'),
  compareStart: null,
  compareEnd: null,
};

// Helper function to determine period type based on dateRangeState
const getPeriodType = (dateRangeState) => {
  if (!dateRangeState?.filter?.id) return 'custom';
  
  const filterId = dateRangeState.filter.id.toLowerCase();
  if (filterId === 'today') return 'today';
  if (filterId === 'yesterday') return 'yesterday';
  if (filterId === 'week' || filterId === 'this week') return 'week';
  if (filterId === 'month' || filterId === 'this month') return 'month';
  if (filterId === 'quarter' || filterId === 'this quarter') return 'quarter';
  if (filterId === 'year' || filterId === 'this year') return 'year';
  return 'custom';
};



// Dashboard Content Component
const DashboardContent = () => {
  const { loading, resetDuplicateCallPrevention, fetchLeadFilters, fetchPendingActions, fetchCriticalMissesToday, fetchLeadsRecovery, fetchActiveLeadsByIntent } = useDashboard();
  const { user } = useAuth();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Initialize date range from URL params or default
  const [dateRangeState, setDateRangeState] = useState(() => {
    const filterId = searchParams.get('filter_id');
    const startDate = searchParams.get('start_date');
    const endDate = searchParams.get('end_date');
    
    if (filterId && startDate && endDate) {
      console.log('[Dashboard] Restoring from URL params:', { filterId, startDate, endDate });
      return {
        filter: { id: filterId, label: filterId },
        currentStart: dayjs(startDate),
        currentEnd: dayjs(endDate),
        compareStart: null,
        compareEnd: null,
      };
    }
    
    console.log('[Dashboard] Using default date range');
    return defaultDateRange;
  });
  
  const [refreshKey, setRefreshKey] = useState(0);
  const [agentLocationRefreshKey, setAgentLocationRefreshKey] = useState(0);
  
  // Initialize teams and agents from URL params
  const [selectedTeams, setSelectedTeams] = useState(() => {
    const teamsParam = searchParams.get('teams');
    if (teamsParam) {
      try {
        const teamIds = teamsParam.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id));
        console.log('[Dashboard] Restoring teams from URL:', teamIds);
        return teamIds;
      } catch (e) {
        console.error('[Dashboard] Error parsing teams from URL:', e);
      }
    }
    return [];
  });
  
  const [selectedAgents, setSelectedAgents] = useState(() => {
    const agentsParam = searchParams.get('agents');
    if (agentsParam) {
      try {
        const agentIds = agentsParam.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id));
        console.log('[Dashboard] Restoring agents from URL:', agentIds);
        return agentIds;
      } catch (e) {
        console.error('[Dashboard] Error parsing agents from URL:', e);
      }
    }
    return [];
  });
  
  const [teams, setTeams] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedLeadSources, setSelectedLeadSources] = useState([]);
  const [leadSourceOptions, setLeadSourceOptions] = useState([
    { value: 'website', label: 'Website' },
    { value: 'referral', label: 'Referral' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'manual', label: 'Manual' },
  ]);
  useEffect(() => {
    fetchLeadFilters().then((data) => {
      if (data?.source && Array.isArray(data.source) && data.source.length > 0) {
        setLeadSourceOptions(data.source.map((s) => ({ value: s.value, label: s.label || s.value })));
      }
    });
  }, [fetchLeadFilters]);
  const [dateFilter, setDateFilter] = useState(() => {
    const filterId = searchParams.get('filter_id');
    return filterId || 'Today';
  });
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(dayjs().startOf('month'));
  const [customEndDate, setCustomEndDate] = useState(dayjs().endOf('day'));
  
  // Refs for trend handlers
  const leadPerformanceTrendRef = useRef(null);
  const callPerformanceTrendRef = useRef(null);
  const lastPendingFetchKeyRef = useRef('');

  // Shared pending-actions data for Pending Action + Active Lead Funnel (single fetch)
  const [pendingActionData, setPendingActionData] = useState(null);
  const [pendingActionLoading, setPendingActionLoading] = useState(true);
  useEffect(() => {
    const teamKey = (selectedTeams || []).join(',');
    const agentKey = (selectedAgents || []).join(',');
    const sourceKey = (selectedLeadSources || []).join(',');
    const fetchKey = `pending_${teamKey}_${agentKey}_${sourceKey}`;
    if (fetchKey === lastPendingFetchKeyRef.current) return;
    lastPendingFetchKeyRef.current = fetchKey;
    setPendingActionLoading(true);
    fetchPendingActions(selectedTeams, selectedAgents, selectedLeadSources)
      .then((response) => {
        setPendingActionData(response ?? null);
      })
      .catch(() => setPendingActionData(null))
      .finally(() => setPendingActionLoading(false));
  }, [selectedTeams, selectedAgents, selectedLeadSources, fetchPendingActions]);

  // Critical Misses Today (single fetch, respects team/agent filter)
  const [criticalMissesData, setCriticalMissesData] = useState(null);
  const [criticalMissesLoading, setCriticalMissesLoading] = useState(true);
  const lastCriticalMissesKeyRef = useRef('');
  useEffect(() => {
    const teamKey = (selectedTeams || []).join(',');
    const agentKey = (selectedAgents || []).join(',');
    const sourceKey = (selectedLeadSources || []).join(',');
    const fetchKey = `critical_misses_${teamKey}_${agentKey}_${sourceKey}`;
    if (fetchKey === lastCriticalMissesKeyRef.current) return;
    lastCriticalMissesKeyRef.current = fetchKey;
    setCriticalMissesLoading(true);
    fetchCriticalMissesToday(selectedTeams, selectedAgents, selectedLeadSources)
      .then((data) => setCriticalMissesData(data ?? null))
      .catch(() => setCriticalMissesData(null))
      .finally(() => setCriticalMissesLoading(false));
  }, [selectedTeams, selectedAgents, selectedLeadSources, fetchCriticalMissesToday]);

  // Leads Recovery (single fetch, respects team/agent filter)
  const [leadsRecoveryData, setLeadsRecoveryData] = useState(null);
  const [leadsRecoveryLoading, setLeadsRecoveryLoading] = useState(true);
  const lastLeadsRecoveryKeyRef = useRef('');
  useEffect(() => {
    const teamKey = (selectedTeams || []).join(',');
    const agentKey = (selectedAgents || []).join(',');
    const sourceKey = (selectedLeadSources || []).join(',');
    const fetchKey = `leads_recovery_${teamKey}_${agentKey}_${sourceKey}`;
    if (fetchKey === lastLeadsRecoveryKeyRef.current) return;
    lastLeadsRecoveryKeyRef.current = fetchKey;
    setLeadsRecoveryLoading(true);
    fetchLeadsRecovery(selectedTeams, selectedAgents, selectedLeadSources)
      .then((data) => setLeadsRecoveryData(data ?? null))
      .catch(() => setLeadsRecoveryData(null))
      .finally(() => setLeadsRecoveryLoading(false));
  }, [selectedTeams, selectedAgents, selectedLeadSources, fetchLeadsRecovery]);

  // Active Leads by Intent (dedicated endpoint)
  const [activeLeadsByIntentData, setActiveLeadsByIntentData] = useState(null);
  const [activeLeadsByIntentLoading, setActiveLeadsByIntentLoading] = useState(true);
  const lastActiveIntentKeyRef = useRef('');
  useEffect(() => {
    const teamKey = (selectedTeams || []).join(',');
    const agentKey = (selectedAgents || []).join(',');
    const sourceKey = (selectedLeadSources || []).join(',');
    const fetchKey = `active_intent_${teamKey}_${agentKey}_${sourceKey}`;
    if (fetchKey === lastActiveIntentKeyRef.current) return;
    lastActiveIntentKeyRef.current = fetchKey;
    setActiveLeadsByIntentLoading(true);
    fetchActiveLeadsByIntent(selectedTeams, selectedAgents, selectedLeadSources)
      .then((data) => setActiveLeadsByIntentData(data ?? null))
      .catch(() => setActiveLeadsByIntentData(null))
      .finally(() => setActiveLeadsByIntentLoading(false));
  }, [selectedTeams, selectedAgents, selectedLeadSources, fetchActiveLeadsByIntent]);

  // Calculate date range based on filter
  const calculateDateRange = (filter) => {
    const today = dayjs();
    switch (filter) {
      case 'Today':
        return {
          startDate: today.startOf('day'),
          endDate: today.endOf('day')
        };
      case 'Yesterday':
        const yesterday = today.subtract(1, 'day');
        return {
          startDate: yesterday.startOf('day'),
          endDate: yesterday.endOf('day')
        };
      case 'This Week':
        return {
          startDate: today.startOf('isoWeek'),
          endDate: today.endOf('day')
        };
      case 'This Month':
        return {
          startDate: today.startOf('month'),
          endDate: today.endOf('day')
        };
      case 'This Quarter':
        const currentQuarter = Math.floor(today.month() / 3);
        const quarterStartMonth = currentQuarter * 3;
        return {
          startDate: today.month(quarterStartMonth).startOf('month'),
          endDate: today.endOf('day')
        };
      case 'This Year':
        return {
          startDate: today.startOf('year'),
          endDate: today.endOf('day')
        };
      default:
        return {
          startDate: dateRangeState.currentStart,
          endDate: dateRangeState.currentEnd
        };
    }
  };

  // Get date range display text
  const getDateRangeDisplay = () => {
    if (!dateRangeState.currentStart || !dateRangeState.currentEnd) return null;
    const start = dayjs(dateRangeState.currentStart);
    const end = dayjs(dateRangeState.currentEnd);
    if (start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')) {
      return start.format('MMM DD, YYYY');
    }
    return `${start.format('MMM DD')} - ${end.format('MMM DD, YYYY')}`;
  };

  // Sync dateFilter with dateRangeState
  useEffect(() => {
    if (dateRangeState.filter?.id) {
      const filterId = dateRangeState.filter.id;
      // Map filter IDs to match the dropdown options
      if (filterId === 'Today' || filterId === 'Yesterday' || filterId === 'This Week' || filterId === 'This Month' || 
          filterId === 'This Quarter' || filterId === 'This Year' || filterId === 'Custom') {
        setDateFilter(filterId);
      } else {
        // If it's a custom date range, set to Custom
        setDateFilter('Custom');
      }
    }
  }, [dateRangeState]);

  // Log when component mounts
  useEffect(() => {
    console.log('[DashboardContent] Component mounted with dateRangeState:', dateRangeState);
    console.log('[DashboardContent] URL params:', Object.fromEntries(searchParams));
    
    return () => {
      console.log('[DashboardContent] Component unmounting');
    };
  }, []);

  // Handle date filter change
  const handleDateFilterChange = (event) => {
    const selectedFilter = event.target.value;
    
    if (selectedFilter === 'Custom') {
      // If Custom is already selected, just open the dialog
      if (dateFilter === 'Custom') {
        if (dateRangeState.currentStart && dateRangeState.currentEnd) {
          setCustomStartDate(dayjs(dateRangeState.currentStart));
          setCustomEndDate(dayjs(dateRangeState.currentEnd));
        } else {
          setCustomStartDate(dayjs().startOf('month'));
          setCustomEndDate(dayjs().endOf('day'));
        }
        setCustomDateDialogOpen(true);
        return;
      }
      
      setDateFilter('Custom');
      if (dateRangeState.currentStart && dateRangeState.currentEnd) {
        setCustomStartDate(dayjs(dateRangeState.currentStart));
        setCustomEndDate(dayjs(dateRangeState.currentEnd));
      } else {
        setCustomStartDate(dayjs().startOf('month'));
        setCustomEndDate(dayjs().endOf('day'));
      }
      setCustomDateDialogOpen(true);
    } else {
      setDateFilter(selectedFilter);
      const newRange = calculateDateRange(selectedFilter);
      const rangeObj = {
        filter: { id: selectedFilter, label: selectedFilter },
        currentStart: newRange.startDate,
        currentEnd: newRange.endDate,
        compareStart: null,
        compareEnd: null,
      };
      handleDateRangeChange(rangeObj);
    }
  };

  // Handle custom date apply
  const handleCustomDateApply = () => {
    const rangeObj = {
      filter: { id: 'Custom', label: 'Custom' },
      currentStart: customStartDate.startOf('day'),
      currentEnd: customEndDate.endOf('day'),
      compareStart: null,
      compareEnd: null,
    };
    handleDateRangeChange(rangeObj);
    setCustomDateDialogOpen(false);
  };

  // Handle custom date dialog close
  const handleCustomDateDialogClose = (event, reason) => {
    if (reason === 'backdropClick') {
      return; // Don't close on backdrop click
    }
    setCustomDateDialogOpen(false);
  };

  // Helper function to update URL with all current filters
  const updateURLParams = (updates = {}) => {
    const params = new URLSearchParams(searchParams);
    
    // Update date range params
    if (updates.dateRange !== undefined) {
      const rangeObj = updates.dateRange;
      if (rangeObj.filter?.id) {
        params.set('filter_id', rangeObj.filter.id);
      }
      if (rangeObj.currentStart) {
        params.set('start_date', rangeObj.currentStart.format('YYYY-MM-DD'));
      }
      if (rangeObj.currentEnd) {
        params.set('end_date', rangeObj.currentEnd.format('YYYY-MM-DD'));
      }
    }
    
    // Update teams param
    if (updates.teams !== undefined) {
      if (updates.teams && updates.teams.length > 0) {
        params.set('teams', updates.teams.join(','));
      } else {
        params.delete('teams');
      }
    }
    
    // Update agents param
    if (updates.agents !== undefined) {
      if (updates.agents && updates.agents.length > 0) {
        params.set('agents', updates.agents.join(','));
      } else {
        params.delete('agents');
      }
    }
    
    setSearchParams(params, { replace: true });
  };

  // Handler for DateRangeFilter (used internally)
  const handleDateRangeChange = (rangeObj) => {
    console.log('[Dashboard] handleDateRangeChange called with:', rangeObj);
    // Reset duplicate call prevention to allow fresh API calls
    resetDuplicateCallPrevention();
    // Update date range state
    setDateRangeState(rangeObj);
    
    // Update URL parameters to persist the selection
    updateURLParams({ 
      dateRange: rangeObj,
      teams: selectedTeams,
      agents: selectedAgents
    });
    
    // Increment refresh key to force all components to refresh
    setRefreshKey(prev => prev + 1);
  };

  // Handler for Teams filter selection
  const handleTeamFilter = (selectedTeamIds) => {
    console.log('Teams filter selected:', selectedTeamIds);
    const newTeams = selectedTeamIds || [];
    setSelectedTeams(newTeams);
    
    // Clear agents when team is selected
    const newAgents = (newTeams.length > 0) ? [] : selectedAgents;
    if (newTeams.length > 0) {
      setSelectedAgents([]);
    }
    
    // Update URL with new team filter
    updateURLParams({ 
      teams: newTeams,
      agents: newAgents
    });
    
    // Increment refresh key to force all components to refresh with new team filter
    setRefreshKey(prev => prev + 1);
  };

  // Handler for Agents filter selection
  const handleAgentFilter = (selectedAgentIds) => {
    console.log('Agents filter selected:', selectedAgentIds);
    const newAgents = selectedAgentIds || [];
    setSelectedAgents(newAgents);
    
    // Clear teams when agent is selected
    const newTeams = (newAgents.length > 0) ? [] : selectedTeams;
    if (newAgents.length > 0) {
      setSelectedTeams([]);
    }
    
    // Update URL with new agent filter
    updateURLParams({ 
      teams: newTeams,
      agents: newAgents
    });
    
    // Increment refresh key to force all components to refresh with new agent filter
    setRefreshKey(prev => prev + 1);
  };

  return (
    <>
      {/* Activity Ticker - Show for admin and manager users */}
      {/* Admin sees all ticker data, Manager sees data from their teams and subteams */}
      {(user?.role === 'AD' || user?.role === 'MG') && (
        <Box sx={{ mb: 3 }}>
          <ActivityTicker />
        </Box>
      )}

      {/* Live Agent Location Section - Admin only */}
      {user?.role === 'AD' && (
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2.5, 
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1.25rem'
            }}
          >
            Live Agent Location
            <Typography
              component="span"
              onClick={() => setAgentLocationRefreshKey(prev => prev + 1)}
              sx={{
                ml: 1.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'primary.main',
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: 3,
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: 'primary.dark',
                  textDecorationThickness: 2,
                },
              }}
            >
              Refresh
            </Typography>
          </Typography>
          <LiveAgentLocation refreshKey={agentLocationRefreshKey} />
        </Box>
      )}

      {/* Filters: top right of section above Critical Misses Today */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
        <Autocomplete
          multiple
          options={leadSourceOptions}
          getOptionLabel={(opt) => (typeof opt === 'string' ? leadSourceOptions.find(o => o.value === opt)?.label ?? opt : opt.label)}
          value={leadSourceOptions.filter((o) => selectedLeadSources.includes(o.value))}
          onChange={(_, newValue) => setSelectedLeadSources(newValue.map((o) => o.value))}
          filterSelectedOptions
          size="small"
          sx={{ minWidth: 200 }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={selectedLeadSources.length === 0 ? 'Search sources...' : ''}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: 2,
                  fontSize: '0.75rem',
                  backgroundColor: '#e3f2fd',
                  borderColor: '#2196f3',
                  color: '#1976d2',
                  minHeight: '32px',
                  paddingTop: 0,
                  paddingBottom: 0,
                  '&:hover': { backgroundColor: '#bbdefb' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2196f3' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                  '& .MuiInputBase-input': { fontSize: '0.75rem', padding: '4px 8px' },
                  '& .MuiAutocomplete-tag': {
                    fontSize: '0.7rem',
                    backgroundColor: 'rgba(33, 150, 243, 0.2)',
                    color: '#1976d2',
                    border: '1px solid #2196f3',
                  },
                },
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <FilterIcon sx={{ fontSize: '0.875rem', color: '#1976d2', mr: 0.5, ml: 1 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.value}>{option.label}</li>
          )}
        />
        <Box sx={{ minWidth: 280 }}>
          <DateRangeFilter
            onChange={() => {}}
            showAllOption={false}
            showTeamFilter={true}
            showAgentFilter={true}
            onTeamProjectFilter={handleTeamFilter}
            onAgentFilter={handleAgentFilter}
            selectedTeams={selectedTeams}
            selectedAgents={selectedAgents}
            setTeams={setTeams}
            setAgents={setAgents}
            hideTimeFilters={true}
          />
        </Box>
      </Box>

      {/* Critical Misses Today - from API */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h6"
          sx={{
            mb: 2.5,
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1.25rem',
          }}
        >
          Critical Misses Today
        </Typography>
        <Grid container spacing={2}>
          {[
            { key: 'followups_missed', label: 'Followups Missed on Hot/Warm Leads', icon: <FollowupMissedIcon />, color: '#ef4444' },
            { key: 'details_not_shared', label: 'Details not shared to Hot/Warm Leads', icon: <DetailsNotSharedIcon />, color: '#f59e0b' },
            { key: 'not_interested', label: 'Hot/Warm Leads marked Not Interested', icon: <NotInterestedIcon />, color: '#8b5cf6' },
            { key: 'site_visit_no_show', label: 'Site Visit No Show', icon: <SiteVisitNoShowIcon />, color: '#dc2626' },
          ].map((item) => {
            const handleCriticalMissCardClick = () => {
              const today = dayjs();
              const params = new URLSearchParams();
              params.set('stat_type', item.key);
              params.set('start_date', today.format('YYYY-MM-DD'));
              params.set('end_date', today.format('YYYY-MM-DD'));
              if (selectedTeams && selectedTeams.length > 0) {
                params.set('teams', selectedTeams.join(','));
              }
              if (selectedAgents && selectedAgents.length > 0) {
                params.set('agents', selectedAgents.join(','));
              }
              if (selectedLeadSources && selectedLeadSources.length > 0) {
                params.set('source', selectedLeadSources.join(','));
              }
              navigate(`/leads?${params.toString()}`, {
                state: {
                  from: 'dashboard',
                  dashboardFilter: searchParams.get('filter_id'),
                  dashboardStartDate: searchParams.get('start_date'),
                  dashboardEndDate: searchParams.get('end_date'),
                  selectedTeams,
                  selectedAgents,
                },
              });
            };
            return (
              <Grid item xs={12} sm={6} md={3} key={item.key}>
                <Paper
                  elevation={0}
                  onClick={handleCriticalMissCardClick}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                    transition: 'box-shadow 0.2s ease',
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 1 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: (t) => alpha(item.color, 0.12),
                        color: item.color,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {criticalMissesLoading ? (
                        <Skeleton variant="text" width={48} height={36} sx={{ mb: 0.5 }} />
                      ) : (
                        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                          {criticalMissesData?.[item.key] ?? 0}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontSize: '0.8rem' }}>
                        {item.label}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Leads Recovery Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h6"
          sx={{
            mb: 2.5,
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1.25rem',
          }}
        >
          Not Interested Leads Recovery
        </Typography>
        <Grid container spacing={2}>
          {[
            {
              key: 'recoverable_ni_leads',
              label: 'Recoverable Leads',
              format: (v) => v ?? 0,
              icon: <RecoverableNILeadsIcon />,
              color: '#059669',
              description: 'Not-interested leads with recovery score ≥ 40%',
              statType: 'recoverable-ni-leads',
            },
            {
              key: 'avg_ni_recovery_score_pct',
              label: 'Avg Recovery Score',
              format: (v) => `${v ?? 0}%`,
              icon: <AvgRecoveryScoreIcon />,
              color: '#6366f1',
              description: 'Average score among NI leads with a model score',
            },
            { 
              key: 'recoverable_value', 
              label: 'Recoverable Value', 
              format: (v) => {
                const num = v ?? 0;
                if (num >= 10000000) return `₹${(num / 10000000).toFixed(1)}Cr`;
                if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
                if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
                return `₹${num.toLocaleString('en-IN')}`;
              },
              icon: <FollowupMissedIcon />,
              color: '#10b981',
              description: 'Total potential revenue from recoverable leads'
            },
            { 
              key: 'active_leads_in_recovery', 
              label: 'Active Leads in Recovery', 
              format: (v) => v ?? 0,
              icon: <DetailsNotSharedIcon />,
              color: '#3b82f6',
              description: 'Leads currently being re-engaged',
              statType: 'active-leads-in-recovery',
            },
          ].map((item) => {
            const handleRecoveryCardClick = () => {
              if (!item.statType) return;
              const params = new URLSearchParams();
              params.set('stat_type', item.statType);
              if (selectedTeams && selectedTeams.length > 0) {
                params.set('teams', selectedTeams.join(','));
              }
              if (selectedAgents && selectedAgents.length > 0) {
                params.set('agents', selectedAgents.join(','));
              }
              if (selectedLeadSources && selectedLeadSources.length > 0) {
                params.set('source', selectedLeadSources.join(','));
              }
              navigate(`/leads?${params.toString()}`, {
                state: {
                  from: 'dashboard',
                  dashboardFilter: searchParams.get('filter_id'),
                  dashboardStartDate: searchParams.get('start_date'),
                  dashboardEndDate: searchParams.get('end_date'),
                  selectedTeams,
                  selectedAgents,
                },
              });
            };
            const isRecoveryDrillDown = Boolean(item.statType);
            return (
            <Grid item xs={12} sm={6} md={3} key={item.key}>
              <Paper
                elevation={0}
                onClick={isRecoveryDrillDown ? handleRecoveryCardClick : undefined}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: (t) => alpha(item.color, 0.04),
                  transition: 'all 0.2s ease',
                  cursor: isRecoveryDrillDown ? 'pointer' : 'default',
                  '&:hover': isRecoveryDrillDown ? { 
                    boxShadow: 2,
                    transform: 'translateY(-2px)',
                  } : {},
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: (t) => alpha(item.color, 0.12),
                      color: item.color,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {leadsRecoveryLoading ? (
                      <Skeleton variant="text" width={48} height={36} sx={{ mb: 0.5 }} />
                    ) : (
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                        {item.format(leadsRecoveryData?.[item.key])}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontSize: '0.8rem' }}>
                      {item.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                      {item.description}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Pending Action Section - filters are above Critical Misses Today */}
      <Box sx={{ mb: 4 }} key={`pending-action-${selectedTeams.join(',')}-${selectedAgents.join(',')}`}>
        <Typography
          variant="h6"
          sx={{ mb: 2.5, fontWeight: 700, color: 'text.primary', fontSize: '1.25rem' }}
        >
          Pending Action
        </Typography>
        <PendingActionStats
          dateRangeState={dateRangeState}
          getPeriodType={getPeriodType}
          selectedTeams={selectedTeams}
          selectedAgents={selectedAgents}
          externalPendingResponse={pendingActionData}
          externalPendingLoading={pendingActionLoading}
        />
      </Box>

      {/* Active Leads by Intent - from dedicated endpoint */}
      <Box sx={{ mb: 4 }} key={`active-funnel-${selectedTeams.join(',')}-${selectedAgents.join(',')}-${selectedLeadSources.join(',')}`}>
        <Typography
          variant="h6"
          sx={{
            mb: 2.5,
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1.25rem',
          }}
        >
          Active Leads by Intent
        </Typography>
        <ActiveLeadFunnel
          selectedTeams={selectedTeams}
          selectedAgents={selectedAgents}
          externalIntentResponse={activeLeadsByIntentData}
          externalIntentLoading={activeLeadsByIntentLoading}
        />
      </Box>

      {/* Lead Performance Section with Date Range Filter */}
      <Box sx={{ mb: 4 }} key={`lead-performance-${refreshKey}`}>
        {/* Header row with title and date filter */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2.5,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1.25rem'
              }}
            >
              Lead Performance
            </Typography>
            <Typography
              onClick={() => {
                if (leadPerformanceTrendRef.current) {
                  leadPerformanceTrendRef.current();
                }
              }}
              sx={{
                color: 'primary.main',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: 3,
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: 'primary.dark',
                  textDecorationThickness: 2,
                },
              }}
            >
              View Trends
            </Typography>
          </Box>
          
          {/* Date Range Filter aligned to the right */}
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateFilter}
              label="Date Range"
              onChange={handleDateFilterChange}
              renderValue={(value) => {
                const getDateRangeText = (filter) => {
                  switch (filter) {
                    case 'Today':
                      return dayjs().format('MMM DD, YYYY');
                    case 'Yesterday':
                      return dayjs().subtract(1, 'day').format('MMM DD, YYYY');
                    case 'This Week':
                      return `${dayjs().startOf('isoWeek').format('MMM DD')} - ${dayjs().format('MMM DD, YYYY')}`;
                    case 'This Month':
                      return `${dayjs().startOf('month').format('MMM DD')} - ${dayjs().format('MMM DD, YYYY')}`;
                    case 'This Quarter':
                      const currentQuarter = Math.floor(dayjs().month() / 3);
                      const quarterStartMonth = currentQuarter * 3;
                      const quarterStart = dayjs().month(quarterStartMonth).startOf('month');
                      return `${quarterStart.format('MMM DD')} - ${dayjs().format('MMM DD, YYYY')}`;
                    case 'This Year':
                      return `${dayjs().startOf('year').format('MMM DD')} - ${dayjs().format('MMM DD, YYYY')}`;
                    case 'Custom':
                      return getDateRangeDisplay() || 'Select date range';
                    default:
                      return '';
                  }
                };
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                      {value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', lineHeight: 1.2 }}>
                      {getDateRangeText(value)}
                    </Typography>
                  </Box>
                );
              }}
              sx={{
                height: 'auto',
                minHeight: '32px',
                fontSize: '0.75rem',
                '& .MuiSelect-select': {
                  py: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                }
              }}
            >
              <MenuItem value="Today">
                <Box>
                  <Typography variant="body2">Today</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {dayjs().format('MMM DD, YYYY')}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="Yesterday">
                <Box>
                  <Typography variant="body2">Yesterday</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {dayjs().subtract(1, 'day').format('MMM DD, YYYY')}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="This Week">
                <Box>
                  <Typography variant="body2">This Week</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {`${dayjs().startOf('isoWeek').format('MMM DD')} - ${dayjs().format('MMM DD, YYYY')}`}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="This Month">
                <Box>
                  <Typography variant="body2">This Month</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {`${dayjs().startOf('month').format('MMM DD')} - ${dayjs().format('MMM DD, YYYY')}`}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="This Quarter">
                <Box>
                  <Typography variant="body2">This Quarter</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {(() => {
                      const currentQuarter = Math.floor(dayjs().month() / 3);
                      const quarterStartMonth = currentQuarter * 3;
                      const quarterStart = dayjs().month(quarterStartMonth).startOf('month');
                      return `${quarterStart.format('MMM DD')} - ${dayjs().format('MMM DD, YYYY')}`;
                    })()}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="This Year">
                <Box>
                  <Typography variant="body2">This Year</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {`${dayjs().startOf('year').format('MMM DD')} - ${dayjs().format('MMM DD, YYYY')}`}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem 
                value="Custom"
                data-value="Custom"
                onClick={(e) => {
                  e.stopPropagation();
                  if (dateFilter === 'Custom') {
                    e.preventDefault();
                    if (dateRangeState.currentStart && dateRangeState.currentEnd) {
                      setCustomStartDate(dayjs(dateRangeState.currentStart));
                      setCustomEndDate(dayjs(dateRangeState.currentEnd));
                    } else {
                      setCustomStartDate(dayjs().startOf('month'));
                      setCustomEndDate(dayjs().endOf('day'));
                    }
                    setCustomDateDialogOpen(true);
                  }
                }}
              >
                <Box>
                  <Typography variant="body2">Custom</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {dateFilter === 'Custom' && getDateRangeDisplay() ? getDateRangeDisplay() : 'Select date range'}
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        <AgentPerformanceStats 
          dateRangeState={dateRangeState} 
          getPeriodType={getPeriodType} 
          selectedTeams={selectedTeams} 
          selectedAgents={selectedAgents}
          onViewTrendsRef={leadPerformanceTrendRef}
        />
      </Box>

      {/* Custom Date Range Dialog */}
      <Dialog 
        open={customDateDialogOpen} 
        onClose={handleCustomDateDialogClose} 
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown={false}
      >
        <DialogTitle>Select Custom Date Range</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              <DatePicker
                label="Start Date"
                value={customStartDate}
                onChange={(newValue) => setCustomStartDate(newValue)}
                maxDate={customEndDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
              <DatePicker
                label="End Date"
                value={customEndDate}
                onChange={(newValue) => setCustomEndDate(newValue)}
                minDate={customStartDate}
                maxDate={dayjs()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCustomDateDialogClose}>Cancel</Button>
          <Button onClick={handleCustomDateApply} variant="contained">Apply</Button>
        </DialogActions>
      </Dialog>

      {/* Call Performance Section */}
      <Box sx={{ mb: 4 }} key={`call-performance-${refreshKey}`}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2.5,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1.25rem'
              }}
            >
              Agent Performance & Discipline
            </Typography>
            <Typography
              onClick={() => {
                if (callPerformanceTrendRef.current) {
                  callPerformanceTrendRef.current();
                }
              }}
              sx={{
                color: 'primary.main',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: 3,
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: 'primary.dark',
                  textDecorationThickness: 2,
                },
              }}
            >
              View Trends
            </Typography>
          </Box>
        </Box>
        <CallPerformanceCards 
          dateRangeState={dateRangeState} 
          selectedTeams={selectedTeams} 
          selectedAgents={selectedAgents}
          onViewTrendsRef={callPerformanceTrendRef}
        />
      </Box>

      {/* Not Interested Reasons Section */}
      <Box sx={{ mb: 4 }} key={`not-interested-reasons-${refreshKey}`}>
        <NotInterestedReasonsChart 
          dateRangeState={dateRangeState}
          selectedTeams={selectedTeams}
          selectedAgents={selectedAgents}
        />
      </Box>

      {/* Sales Performance Section */}
      <Box sx={{ mb: 4 }} key={`sales-performance-${refreshKey}`}>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 2.5, 
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1.25rem'
          }}
        >
          Sales Performance
        </Typography>
        <StatsGrid dateRangeState={dateRangeState} getPeriodType={getPeriodType} selectedTeams={selectedTeams} />
      </Box>

    </>
  );
};

const DashboardBase = () => {
  return (
    <DashboardProvider>
      <Box 
        sx={{ 
          flexGrow: 1,
          py: 3,
          px: { xs: 2, sm: 3 }
        }}
      >
        <DashboardContent />
      </Box>
    </DashboardProvider>
  );
};

export default DashboardBase;